import config from "@~/envConfig";
import { OnlineStockData, Product, ProductListConfig } from "@~/types/types";
import { getVariant } from "@~/contexts/VariantContext/utils";
import { reportClientError } from "@~/utils/reportClientError";

const API_URL = config.onlineStockBaseUrl;
const API_KEY = config.onlineStockApiKey;

export async function fetchOnlineStockDataForProducts(
  products: Product[],
  configName: string | undefined,
  consumerId: string,
  instanceId?: string,
): Promise<OnlineStockData[]> {
  const skuIds = products
    .map((product) => {
      const variant = getVariant({
        product,
        userSelectedColour: null,
      });
      return variant?.aliases.skuId;
    })
    .filter((skuID) => skuID !== undefined);
  return fetchOnlineStockData(skuIds, configName, consumerId, instanceId);
}

export async function fetchOnlineStockData(
  skuIds: string[],
  configName: string | undefined,
  consumerId: string,
  instanceId?: string,
): Promise<OnlineStockData[]> {
  const url = `${API_URL}?key=${API_KEY}&skuIds=${skuIds.join(",")}`;

  const newrelicAttributes = {
    api: "onlineStock",
    skuIds: skuIds.join(","),
    config: configName,
    consumerId,
    instanceId,
  };

  let res: Response;
  try {
    res = await fetch(url, { credentials: "include" });
  } catch (error) {
    reportClientError(error, newrelicAttributes);
    throw error;
  }

  if (!res.ok) {
    const error = new Error("Error calling Online Stock service");
    reportClientError(error, {
      ...newrelicAttributes,
      status: res.status,
    });
    throw error;
  }

  return (
    (await res.json().then(({ stocks }) => validateOnlineStockData(stocks))) ??
    []
  );
}

const validateOnlineStockData = (data: unknown): OnlineStockData[] | null => {
  if (!Array.isArray(data)) return null;

  return data.every((item) => {
    if (typeof item !== "object" || item === null) return false;
    const { availabilityStatus, stockMessage, stockQuantity, skuId } = item;
    if (typeof availabilityStatus !== "string") return false;
    if (typeof stockMessage !== "string") return false;
    if (typeof stockQuantity !== "number") return false;
    if (typeof skuId !== "string") return false;
    return true;
  })
    ? data
    : null;
};

export const shouldFetchOnlineStock = (config: ProductListConfig) => {
  return isComponentInConfig("StockMessage", config);
};

const isComponentInConfig = (
  componentName: string,
  config: ProductListConfig,
) => {
  const listOfComponentsInConfig = Object.values(
    config["product-card-slots"] ?? {},
  )
    .flat()
    .map(({ name }) => name);

  return listOfComponentsInConfig.includes(componentName);
};
